import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllActualitesAdmin,
  createActualite,
  deleteActualite,
  updateActualite,
  isAdminLoggedIn,
  logoutAdmin,
} from '../services/api';
import '../styles/Actualites.css';

function AdminActualites() {
  const [actualites, setActualites] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  // Chaque preview : { file?: File, url: string, isExisting: boolean, serverPath?: string }
  const [imagePreviews, setImagePreviews] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'projet',
    type: '',
    date: '',
    links: [{ url: '', label: '' }],
    tags: '',
  });

  const navigate = useNavigate();
  const API_BASE_URL =
    process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:5001';

  useEffect(() => {
    if (!isAdminLoggedIn()) {
      navigate('/admin/login');
      return;
    }
    fetchActualites();
  }, [navigate]);

  const fetchActualites = async () => {
    try {
      const response = await getAllActualitesAdmin();
      setActualites(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_BASE_URL}${imagePath}`;
  };

  // ===========================
  // GESTION DES IMAGES
  // ===========================

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const totalAfterAdd = imagePreviews.length + files.length;

    if (totalAfterAdd > 10) {
      alert(`Maximum 10 images. Vous en avez déjà ${imagePreviews.length}.`);
      return;
    }

    const validFiles = [];
    const rejected = [];

    files.forEach((file) => {
      if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/)) {
        rejected.push(`${file.name} : format non supporté`);
      } else if (file.size > 5 * 1024 * 1024) {
        rejected.push(`${file.name} : dépasse 5MB`);
      } else {
        validFiles.push(file);
      }
    });

    if (rejected.length > 0) {
      alert('Fichiers ignorés :\n' + rejected.join('\n'));
    }

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [
          ...prev,
          { file, url: reader.result, isExisting: false },
        ]);
      };
      reader.readAsDataURL(file);
    });

    // Reset l'input pour pouvoir re-sélectionner les mêmes fichiers
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // ===========================
  // GESTION DES LIENS
  // ===========================

  const handleLinkChange = (index, field, value) => {
    const newLinks = [...formData.links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setFormData({ ...formData, links: newLinks });
  };

  const addLink = () => {
    setFormData({
      ...formData,
      links: [...formData.links, { url: '', label: '' }],
    });
  };

  const removeLink = (index) => {
    const newLinks = formData.links.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      links: newLinks.length > 0 ? newLinks : [{ url: '', label: '' }],
    });
  };

  // ===========================
  // SUBMIT / EDIT / CANCEL
  // ===========================

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const tags = formData.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const links = formData.links.filter((l) => l.url.trim());

      if (editMode) {
        await updateActualite(editId, {
          title: formData.title,
          description: formData.description,
          category: formData.category,
          type: formData.type,
          date: formData.date,
          eventDate: formData.eventDate,
          tags,
          links,
          existingImages: imagePreviews
            .filter((p) => p.isExisting)
            .map((p) => p.serverPath),
          newImages: imagePreviews
            .filter((p) => !p.isExisting)
            .map((p) => p.file),
        });
      } else {
        await createActualite({
          title: formData.title,
          description: formData.description,
          category: formData.category,
          type: formData.type,
          date: formData.date,
          eventDate: formData.eventDate,
          tags,
          links,
          images: imagePreviews.map((p) => p.file).filter(Boolean),
        });
      }

      handleCancelEdit();
      fetchActualites();
    } catch (error) {
      alert(`Erreur lors de ${editMode ? 'la modification' : 'la création'}`);
      console.error(error);
    }
  };

  const handleEdit = (actualite) => {
    setEditMode(true);
    setEditId(actualite._id);
    setFormData({
      title: actualite.title,
      description: actualite.description,
      category: actualite.category,
      type: actualite.type,
      date: actualite.date,
      eventDate: actualite.eventDate ? actualite.eventDate.split('T')[0] : '',
      links:
        actualite.links && actualite.links.length > 0
          ? actualite.links.map((l) => ({ url: l.url || '', label: l.label || '' }))
          : [{ url: '', label: '' }],
      tags: actualite.tags.join(', '),
    });

    // Charger les images existantes en preview
    const existingPreviews = (actualite.images || []).map((img) => ({
      file: null,
      url: getImageUrl(img),
      isExisting: true,
      serverPath: img,
    }));
    setImagePreviews(existingPreviews);
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditId(null);
    setShowForm(false);
    setImagePreviews([]);
    setFormData({
      title: '',
      description: '',
      category: 'projet',
      type: '',
      date: '',
      eventDate: '',
      links: [{ url: '', label: '' }],
      tags: '',
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cette actualité ?')) {
      try {
        await deleteActualite(id);
        fetchActualites();
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const handleStatusChange = async (id, statut) => {
    try {
      await updateActualite(id, { statut });
      fetchActualites();
    } catch (error) {
      alert('Erreur');
    }
  };

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin/login');
  };

  // ===========================
  // RENDER
  // ===========================

  return (
    <div className="admin-actualites">
      <div className="header">
        <h1>Gestion des Actualités</h1>
        <div>
          <button onClick={() => navigate('/admin/dashboard')} className="btn-secondary">
            ← Dashboard
          </button>
          <button
            onClick={() => {
              if (!showForm) {
                setShowForm(true);
                setEditMode(false);
              } else {
                handleCancelEdit();
              }
            }}
            className="btn-primary"
          >
            {showForm ? 'Annuler' : '+ Nouvelle Actualité'}
          </button>
          <button onClick={handleLogout} className="btn-logout">
            Déconnexion
          </button>
        </div>
      </div>

      {/* ===== FORMULAIRE ===== */}
      {showForm && (
        <div className="form-card">
          <h2>{editMode ? "Modifier l'actualité" : 'Créer une actualité'}</h2>
          <form onSubmit={handleSubmit}>
            {/* Titre */}
            <div className="form-group">
              <label>Titre *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Construction d'une école à Anjouan"
                required
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Décrivez l'actualité..."
                rows="4"
                required
              />
            </div>

            {/* Catégorie / Type / Date */}
            <div className="form-row">
              <div className="form-group">
                <label>Catégorie *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="projet">Projet</option>
                  <option value="evenement">Événement</option>
                  <option value="presse">Presse</option>
                  <option value="permanence">Permanence Numérique</option>
                </select>
              </div>
              <div className="form-group">
                <label>Type *</label>
                <input
                  type="text"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  placeholder="Ex: Projet en cours"
                  required
                />
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="text"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  placeholder="Ex: Mars 2024"
                  required
                />
              </div>
              <div className="form-group">
                <label>📅 Date réelle (pour le tri)</label>
                <input
                  type="date"
                  value={formData.eventDate}
                  onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                />
              </div>
            </div>

            {/* ===== LIENS MULTIPLES ===== */}
            <div className="form-group">
              <label>🔗 Liens externes</label>
              {formData.links.map((link, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '8px',
                    alignItems: 'center',
                  }}
                >
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                    placeholder="https://exemple.com/article"
                    style={{ flex: 2 }}
                  />
                  <input
                    type="text"
                    value={link.label}
                    onChange={(e) => handleLinkChange(index, 'label', e.target.value)}
                    placeholder="Libellé (optionnel)"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => removeLink(index)}
                    className="btn-delete"
                    style={{ padding: '8px 12px', minWidth: 'auto' }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addLink}
                className="btn-secondary"
                style={{ marginTop: '4px' }}
              >
                + Ajouter un lien
              </button>
            </div>

            {/* ===== IMAGES MULTIPLES ===== */}
            <div className="form-group">
              <label>📷 Images ({imagePreviews.length}/10)</label>
              <div className="image-upload-container">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImagesChange}
                  id="image-upload"
                  className="image-input"
                  multiple
                />
                <label htmlFor="image-upload" className="image-upload-label">
                  📷 Ajouter des images
                </label>
                <span className="image-hint">Max 5MB chacune — JPG, PNG, GIF, WebP</span>
              </div>

              {imagePreviews.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    marginTop: '12px',
                  }}
                >
                  {imagePreviews.map((preview, index) => (
                    <div
                      key={index}
                      style={{
                        position: 'relative',
                        width: '150px',
                      }}
                    >
                      <img
                        src={preview.url}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '2px solid #ddd',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          background: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        ✕
                      </button>
                      {preview.isExisting && (
                        <span
                          style={{
                            position: 'absolute',
                            bottom: '4px',
                            left: '4px',
                            background: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                          }}
                        >
                          Existante
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="form-group">
              <label>Tags (séparés par des virgules)</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="Éducation, Partenariat, Infrastructure"
              />
            </div>

            <button type="submit" className="btn-submit">
              {editMode ? 'Enregistrer les modifications' : "Créer l'actualité"}
            </button>
          </form>
        </div>
      )}

      {/* ===== LISTE DES ACTUALITÉS ===== */}
      <div className="actualites-list">
        <h2>Liste des actualités ({actualites.length})</h2>
        {actualites.length === 0 ? (
          <p>Aucune actualité créée</p>
        ) : (
          <div className="cards-grid">
            {actualites.map((a) => (
              <div key={a._id} className={`actualite-card ${a.statut}`}>
                {/* Images */}
                {a.images && a.images.length > 0 ? (
                  <div
                    className="card-image"
                    style={{
                      display: 'flex',
                      gap: '4px',
                      overflowX: 'auto',
                      padding: '4px 0',
                    }}
                  >
                    {a.images.map((img, i) => (
                      <img
                        key={i}
                        src={getImageUrl(img)}
                        alt={`${a.title} ${i + 1}`}
                        style={{
                          height: '160px',
                          borderRadius: '8px',
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="card-image-placeholder">📷</div>
                )}

                {/* Header */}
                <div className="card-header">
                  <h3>{a.title}</h3>
                  <select
                    value={a.statut}
                    onChange={(e) => handleStatusChange(a._id, e.target.value)}
                    className="status-select"
                  >
                    <option value="active">Active</option>
                    <option value="archivee">Archivée</option>
                  </select>
                </div>

                <p className="description">{a.description}</p>

                {/* Liens */}
                {a.links && a.links.length > 0 && (
                  <div
                    className="link-info"
                    style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
                  >
                    {a.links.map((link, i) => (
                      <div key={i}>
                        <span>🔗</span>{' '}
                        <a href={link.url} target="_blank" rel="noopener noreferrer">
                          {link.label ||
                            (link.url.length > 40
                              ? link.url.substring(0, 40) + '...'
                              : link.url)}
                        </a>
                      </div>
                    ))}
                  </div>
                )}

                {/* Infos */}
                <div className="info">
                  <div>📅 {a.date}</div>
                  <div>📂 {a.category}</div>
                  <div>🏷️ {a.type}</div>
                </div>

                {/* Tags */}
                <div className="tags">
                  {a.tags.map((tag, i) => (
                    <span key={i} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button onClick={() => handleEdit(a)} className="btn-edit">
                    Modifier
                  </button>
                  <button onClick={() => handleDelete(a._id)} className="btn-delete">
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminActualites;